/**
 * Phase 4+ — Compliance Engine
 * 
 * Evaluates case compliance status and determines if a case
 * is defensible for audit review.
 * 
 * NON-NEGOTIABLE RULES:
 * - If documentation conflicts exist, case remains blocked
 * - If surgeon defers confirmation, export remains blocked
 * - If payer rules conflict, flag REVIEW_REQUIRED
 * - Never auto-resolve conflicts silently
 */

import type { CaseComplianceStatus } from './types';

export interface ComplianceInput {
  caseId: string;
  hasSurgeonAttestation: boolean;
  attestationTimestamp?: string;
  authorizedCpts: string[];
  cptStates: Record<string, {
    surgeonConfirmation: string | null;
    billableStatus: string;
    documentationStatus?: string;
  }>;
  hasOpNote: boolean;
  hasAnalysis: boolean;
  hasConflicts: boolean;
  overrideCount: number;
  exportGatesPass: boolean;
  isDemoMode: boolean;
}

/**
 * Evaluate full case compliance status.
 */
export function evaluateCaseCompliance(input: ComplianceInput): CaseComplianceStatus {
  const blockingReasons: string[] = [];

  // Check attestation
  const attestationsComplete = input.hasSurgeonAttestation;
  if (!attestationsComplete) {
    blockingReasons.push('Missing surgeon attestation for authorization bundle');
  }

  // Check all CPTs confirmed
  const confirmedCpts = input.authorizedCpts.filter(cpt => {
    const state = input.cptStates[cpt];
    return state?.surgeonConfirmation === 'confirmed_performed' || 
           state?.surgeonConfirmation === 'not_performed' ||
           state?.surgeonConfirmation === 'not_applicable';
  });
  const allCptsConfirmed = confirmedCpts.length === input.authorizedCpts.length;
  if (!allCptsConfirmed) {
    const pending = input.authorizedCpts.length - confirmedCpts.length;
    blockingReasons.push(`${pending} CPT(s) awaiting surgeon confirmation`);
  }

  // Check for deferred CPTs (blocks export)
  const deferredCpts = input.authorizedCpts.filter(cpt => {
    const state = input.cptStates[cpt];
    return state?.surgeonConfirmation === 'deferred';
  });
  if (deferredCpts.length > 0) {
    blockingReasons.push(`${deferredCpts.length} CPT(s) deferred by surgeon — export blocked until resolved`);
  }

  // Check for documentation conflicts
  if (input.hasConflicts) {
    blockingReasons.push('Documentation conflicts detected — case remains blocked until resolved');
  }

  // Check documentation status for confirmed CPTs
  const insufficientDocs = input.authorizedCpts.filter(cpt => {
    const state = input.cptStates[cpt];
    return state?.surgeonConfirmation === 'confirmed_performed' &&
           state?.documentationStatus !== 'sufficient' &&
           state?.documentationStatus !== undefined;
  });
  if (insufficientDocs.length > 0) {
    blockingReasons.push(`${insufficientDocs.length} confirmed CPT(s) have insufficient documentation`);
  }

  // Check op note
  if (!input.hasOpNote) {
    blockingReasons.push('No operative note uploaded');
  }

  // Check analysis
  if (!input.hasAnalysis) {
    blockingReasons.push('Reconciliation analysis not yet completed');
  }

  // Determine overall status
  let status: CaseComplianceStatus['status'];
  if (input.isDemoMode) {
    // In demo mode, we still evaluate but don't block
    status = blockingReasons.length === 0 ? 'compliant' : 'review_required';
  } else if (blockingReasons.length > 0) {
    status = 'blocked';
  } else if (input.overrideCount > 0) {
    status = 'review_required';
  } else {
    status = 'compliant';
  }

  return {
    caseId: input.caseId,
    attestationsComplete,
    allCptsConfirmed,
    exportGatesPass: input.exportGatesPass,
    hasConflicts: input.hasConflicts,
    hasOverrides: input.overrideCount > 0,
    overrideCount: input.overrideCount,
    auditTimelineComplete: input.hasOpNote && input.hasAnalysis && attestationsComplete,
    status,
    blockingReasons,
  };
}
